import { getUserAttributes } from '@/lib/authSession'

/** Known paid subscription attribute names in JWT `aud`. */
export const SUBSCRIPTION_PLANS = [
    'proSubscription',
    'coachSubscription',
    'influencerSubscription'
] as const

export type SubscriptionPlan = (typeof SUBSCRIPTION_PLANS)[number]
export type CurrentSubscription = SubscriptionPlan | 'free'

/** Higher wins when multiple active plans exist. */
const PLAN_PRIORITY: Record<SubscriptionPlan, number> = {
    proSubscription: 1,
    coachSubscription: 2,
    influencerSubscription: 3
}

type ParsedSubscriptionAttr = {
    name: SubscriptionPlan
    issued: number
    deadline: number
}

const SUBSCRIPTION_CLOCK_SKEW_SECONDS = 5 * 60

export function isSubscriptionPlan(name: string): name is SubscriptionPlan {
    return (SUBSCRIPTION_PLANS as readonly string[]).includes(name)
}

/**
 * Parse `planName:issued:deadline` (unix seconds).
 * Missing / invalid deadline → inactive.
 */
export function parseSubscriptionAttr(
    attr: string,
    nowSeconds: number = Date.now() / 1000
): ParsedSubscriptionAttr | null {
    const parts = attr.split(':')
    const name = parts[0]
    if (!isSubscriptionPlan(name)) return null

    const issued = Number(parts[1])
    const deadline = Number(parts[2])
    if (!Number.isFinite(issued) || !Number.isFinite(deadline)) return null
    if (deadline + SUBSCRIPTION_CLOCK_SKEW_SECONDS <= nowSeconds) return null

    return { name, issued, deadline }
}

function getActiveSubscriptions(
    nowSeconds: number = Date.now() / 1000
): ParsedSubscriptionAttr[] {
    return getUserAttributes()
        .map((attr) => parseSubscriptionAttr(attr, nowSeconds))
        .filter((parsed): parsed is ParsedSubscriptionAttr => parsed !== null)
}

function pickPrimarySubscription(
    active: ParsedSubscriptionAttr[]
): ParsedSubscriptionAttr | null {
    if (active.length === 0) return null
    // Same plan granted more than once (renewals, overlapping grants): the
    // latest deadline is the one that actually bounds access.
    return active.reduce((best, current) => {
        const byPriority =
            PLAN_PRIORITY[current.name] - PLAN_PRIORITY[best.name]
        if (byPriority !== 0) return byPriority > 0 ? current : best
        return current.deadline > best.deadline ? current : best
    })
}

export type PremiumAccessTimeline = {
    isPremium: boolean
    issued: number
    deadline: number
}

/** Primary active plan timeline. Expired / unknown → not premium. */
export function getPremiumAccessTimelines(): PremiumAccessTimeline {
    const primary = pickPrimarySubscription(getActiveSubscriptions())
    if (!primary) {
        return { isPremium: false, issued: 0, deadline: 0 }
    }
    return {
        isPremium: true,
        issued: primary.issued,
        deadline: primary.deadline
    }
}

export function isPremiumUser(): boolean {
    return getActiveSubscriptions().length > 0
}

export function isCoach(): boolean {
    return getActiveSubscriptions().some((s) => s.name === 'coachSubscription')
}

export function isInfluencer(): boolean {
    return getActiveSubscriptions().some(
        (s) => s.name === 'influencerSubscription'
    )
}

/**
 * Business attributes in JWT `aud`, granted by approving a business
 * application. They are not subscription plans: they carry no price and do
 * not take part in plan priority.
 */
export const BUSINESS_ATTRIBUTES = [
    'businessOwner',
    'businessBranch',
    'businessUnit'
] as const

/** Any active business attribute (owner, branch or unit). */
export function isBusiness(nowSeconds: number = Date.now() / 1000): boolean {
    return getUserAttributes().some((attr) => {
        const [name, , rawDeadline] = attr.split(':')
        if (!(BUSINESS_ATTRIBUTES as readonly string[]).includes(name)) {
            return false
        }
        const deadline = Number(rawDeadline)
        return (
            Number.isFinite(deadline) &&
            deadline + SUBSCRIPTION_CLOCK_SKEW_SECONDS > nowSeconds
        )
    })
}

/** Highest-priority active plan name, or `free`. */
export function getCurrentSubscription(): CurrentSubscription {
    const primary = pickPrimarySubscription(getActiveSubscriptions())
    return primary?.name ?? 'free'
}
